import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Search, X } from 'lucide-react';
import { Chip, Input, Select } from '@/ui/primitives';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api-client';
import { useBoard } from '../api';
import { TEMP_CHIP } from '../temperatura';
import { CrmDetailModal } from './CrmDetailModal';
import {
  ESTAGIO_LABEL,
  FRENTE_LABEL,
  FRENTES,
  TEMPERATURA_LABEL,
  TEMPERATURAS,
  type Card as CardData,
  type Estagio,
  type Frente,
  type Temperatura,
} from '../schemas';

type SortKey =
  | 'cliente_nome'
  | 'municipio'
  | 'estagio'
  | 'frente'
  | 'temperatura'
  | 'servico'
  | 'imovel_denominacao'
  | 'potencial_max'
  | 'dias_na_coluna'
  | 'comercial_nome';

interface SortState {
  key: SortKey;
  dir: 'asc' | 'desc';
}

const ESTAGIO_TONE: Partial<Record<Estagio, string>> = {
  sinal_detectado: 'bg-surface2 text-text-2',
  qualificacao: 'bg-[rgba(217,119,87,0.10)] text-accent',
  dossie: 'bg-[rgba(217,119,87,0.10)] text-accent',
  abordagem: 'bg-[rgba(0,113,227,0.10)] text-[var(--info)]',
  proposta: 'bg-[rgba(16,185,129,0.10)] text-[var(--ok)]',
  congelado: 'bg-[rgba(0,113,227,0.10)] text-[var(--info)]',
  fechado_ganho: 'bg-[rgba(16,185,129,0.10)] text-[var(--ok)]',
  fechado_perdido: 'bg-[rgba(220,38,38,0.10)] text-[var(--danger)]',
  recusado: 'bg-[rgba(220,38,38,0.10)] text-[var(--danger)]',
  descartado: 'bg-surface2 text-text-muted',
  triagem: 'bg-surface2 text-text-2',
  falso_positivo_servico: 'bg-[rgba(234,179,8,0.14)] text-[#a16207] dark:text-[#facc15]',
};

const TEMP_CLASS: Record<Temperatura, string> = {
  quente: TEMP_CHIP.quente,
  morna: TEMP_CHIP.morna,
  fria: TEMP_CHIP.fria,
};

function fmtMoeda(min: number | null | undefined, max: number | null | undefined) {
  const m = max ?? min;
  if (!m) return '—';
  return m.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

// View de lista — todas as oportunidades em tabela, com filtros e ordenação.
// Cobre o gap #11 do backlog. Click em linha abre o modal padrão de detalhe.
export function CrmListView() {
  const [frente, setFrente] = useState<Frente | ''>('');
  const [temperatura, setTemperatura] = useState<Temperatura | ''>('');
  const [estagioFiltro, setEstagioFiltro] = useState<Estagio | ''>('');
  const [search, setSearch] = useState('');
  const [incluirFechados, setIncluirFechados] = useState(false);
  const [sort, setSort] = useState<SortState>({ key: 'dias_na_coluna', dir: 'desc' });
  const [selected, setSelected] = useState<CardData | null>(null);

  const { data, isLoading, error } = useBoard(frente);

  const todasCards = useMemo<CardData[]>(() => {
    if (!data) return [];
    const flat: CardData[] = [];
    for (const col of data.colunas) {
      for (const c of col.processos) flat.push(c);
    }
    return flat;
  }, [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return todasCards.filter((c) => {
      if (!incluirFechados && (c.estagio === 'fechado_ganho' || c.estagio === 'fechado_perdido' || c.estagio === 'recusado' || c.estagio === 'descartado')) {
        return false;
      }
      if (estagioFiltro && c.estagio !== estagioFiltro) return false;
      if (temperatura && c.temperatura !== temperatura) return false;
      if (q) {
        const hay = [
          c.cliente_nome, c.cliente_cpf, c.imovel_denominacao,
          c.imovel_matricula, c.verificacao, c.servico, c.municipio, c.comercial_nome,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [todasCards, search, temperatura, estagioFiltro, incluirFechados]);

  const sorted = useMemo(() => {
    const out = [...filtered];
    out.sort((a, b) => {
      const ka = (a as Record<SortKey, unknown>)[sort.key];
      const kb = (b as Record<SortKey, unknown>)[sort.key];
      const va = ka == null ? '' : ka;
      const vb = kb == null ? '' : kb;
      let cmp: number;
      if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb), 'pt-BR', { numeric: true });
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return out;
  }, [filtered, sort]);

  function toggleSort(key: SortKey) {
    setSort((s) => s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' });
  }

  function limpar() {
    setFrente(''); setTemperatura(''); setEstagioFiltro(''); setSearch(''); setIncluirFechados(false);
  }

  const filtroAtivo = !!frente || !!temperatura || !!estagioFiltro || !!search.trim() || incluirFechados;

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px] md:max-w-md">
            <Search size={14} strokeWidth={1.5} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cliente, CPF, imóvel, serviço, município, comercial…"
              className="rounded-full pr-9"
            />
          </div>
          <Select value={frente} onChange={(e) => setFrente(e.target.value as Frente | '')} className="w-auto">
            <option value="">Frente: todas</option>
            {FRENTES.map((f) => <option key={f} value={f}>{FRENTE_LABEL[f]}</option>)}
          </Select>
          <Select value={estagioFiltro} onChange={(e) => setEstagioFiltro(e.target.value as Estagio | '')} className="w-auto">
            <option value="">Estágio: todos</option>
            {(Object.keys(ESTAGIO_LABEL) as Estagio[]).map((s) => (
              <option key={s} value={s}>{ESTAGIO_LABEL[s]}</option>
            ))}
          </Select>
          <Select value={temperatura} onChange={(e) => setTemperatura(e.target.value as Temperatura | '')} className="w-auto">
            <option value="">Temperatura: todas</option>
            {TEMPERATURAS.map((t) => <option key={t} value={t}>{TEMPERATURA_LABEL[t]}</option>)}
          </Select>
          <label className="flex items-center gap-1.5 text-[0.78rem] text-text-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={incluirFechados}
              onChange={(e) => setIncluirFechados(e.target.checked)}
              className="rounded border-border2"
            />
            Incluir fechados
          </label>
          {filtroAtivo && (
            <button type="button" onClick={limpar} className="text-[0.78rem] text-text-muted hover:text-accent hover:underline">
              <X size={11} className="inline mr-0.5" /> Limpar
            </button>
          )}
          <Chip tone="neutral" size="sm" className="ml-auto">
            {sorted.length} {sorted.length === 1 ? 'oportunidade' : 'oportunidades'}
          </Chip>
        </div>

        {isLoading && <p className="text-sm text-text-muted py-6">carregando…</p>}
        {error && (
          <p className="text-sm text-danger py-6">
            {error instanceof ApiError ? error.message : 'erro ao carregar oportunidades'}
          </p>
        )}

        {!isLoading && !error && (
          <div className="border border-border rounded-lg overflow-hidden bg-surface">
            <div className="overflow-x-auto">
              <table className="w-full text-[0.82rem]">
                <thead>
                  <tr className="bg-surface2 text-text-2 text-left text-[0.72rem] uppercase tracking-wider">
                    <Th label="Cliente" sortKey="cliente_nome" sort={sort} onToggle={toggleSort} />
                    <Th label="Município" sortKey="municipio" sort={sort} onToggle={toggleSort} />
                    <Th label="Estágio" sortKey="estagio" sort={sort} onToggle={toggleSort} />
                    <Th label="Frente" sortKey="frente" sort={sort} onToggle={toggleSort} />
                    <Th label="Serviço" sortKey="servico" sort={sort} onToggle={toggleSort} />
                    <Th label="Imóvel" sortKey="imovel_denominacao" sort={sort} onToggle={toggleSort} />
                    <Th label="Temp." sortKey="temperatura" sort={sort} onToggle={toggleSort} />
                    <Th label="Comercial" sortKey="comercial_nome" sort={sort} onToggle={toggleSort} />
                    <Th label="Potencial" sortKey="potencial_max" sort={sort} onToggle={toggleSort} numeric />
                    <Th label="Dias" sortKey="dias_na_coluna" sort={sort} onToggle={toggleSort} numeric />
                  </tr>
                </thead>
                <tbody>
                  {sorted.length === 0 && (
                    <tr>
                      <td colSpan={10} className="text-center py-8 text-text-muted text-[0.82rem]">
                        Nenhuma oportunidade corresponde aos filtros.
                      </td>
                    </tr>
                  )}
                  {sorted.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => setSelected(c)}
                      className="border-t border-border hover:bg-[rgba(217,119,87,0.04)] cursor-pointer transition-colors"
                    >
                      <td className="px-3 py-2 max-w-[180px] truncate" title={c.cliente_nome ?? c.cliente_cpf}>
                        <div className="font-medium text-text truncate">{c.cliente_nome ?? c.cliente_cpf}</div>
                        {c.cliente_cpf && c.cliente_nome && (
                          <div className="text-[0.7rem] text-text-muted truncate">{c.cliente_cpf}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 max-w-[120px] truncate text-text-2" title={c.municipio ?? ''}>
                        {c.municipio ?? '—'}
                      </td>
                      <td className="px-3 py-2">
                        <span className={cn('inline-block px-1.5 py-0.5 rounded text-[0.7rem] font-semibold', ESTAGIO_TONE[c.estagio] ?? 'bg-surface2 text-text-2')}>
                          {ESTAGIO_LABEL[c.estagio]}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-text-2 capitalize">{FRENTE_LABEL[c.frente]}</td>
                      <td className="px-3 py-2 max-w-[240px] truncate text-text" title={c.servico}>{c.servico}</td>
                      <td className="px-3 py-2 max-w-[140px] truncate text-text-2" title={c.imovel_denominacao ?? ''}>
                        {c.imovel_denominacao ?? '—'}
                      </td>
                      <td className="px-3 py-2">
                        {c.temperatura ? (
                          <span className={cn('inline-block px-1.5 py-0.5 rounded text-[0.7rem] font-medium capitalize', TEMP_CLASS[c.temperatura])}>
                            {c.temperatura}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-2 max-w-[140px] truncate text-text-2" title={c.comercial_nome ?? ''}>
                        {c.comercial_nome ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-text tabular-nums">
                        {fmtMoeda(c.potencial_min, c.potencial_max)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <span className={cn(
                          c.dias_na_coluna > 14 && 'text-danger font-semibold',
                          c.dias_na_coluna > 7 && c.dias_na_coluna <= 14 && 'text-warn',
                        )}>
                          {c.dias_na_coluna}d
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <CrmDetailModal card={selected} onClose={() => setSelected(null)} />
    </>
  );
}

// ─── header de coluna com sort ───────────────────────────────────────────────

interface ThProps {
  label: string;
  sortKey: SortKey;
  sort: SortState;
  onToggle: (k: SortKey) => void;
  numeric?: boolean;
}

function Th({ label, sortKey, sort, onToggle, numeric }: ThProps) {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      onClick={() => onToggle(sortKey)}
      className={cn(
        'px-3 py-2 font-semibold cursor-pointer select-none whitespace-nowrap',
        numeric && 'text-right',
        active && 'text-accent',
      )}
    >
      <span className={cn('inline-flex items-center gap-1', numeric && 'flex-row-reverse')}>
        {label}
        <Icon size={10} className={active ? 'opacity-100' : 'opacity-40'} />
      </span>
    </th>
  );
}
